import "./_engagement.scss";

import {useContext, useEffect, useState} from "react";
import {useFieldArray} from "react-hook-form";
import {useSearchParams} from "react-router-dom";

import {fetchAdresse} from "@/api/adresse";
import {postEngagements} from "@/api/engagement";
import iconAdd from "@/assets/massification/iconAdd.svg";
import iconValidate from "@/assets/massification/iconValidate.svg";
import BlocLabel from "@/components/BlocLabel/BlocLabel";
import Button from "@/components/Button/Button";
import {Error as ErrorMessageComponent} from "@/components/Error/Error";
import Loader from "@/components/Loader/loader";
import {Modale} from "@/components/Modale/Modale";
import Section from "@/components/Section/Section";
import {HookFormContext} from "@/context/HookFormContext";
import {findAdresse} from "@/function/engagementTools";
import {findDataArticle} from "@/function/findDataArticle";
import {formatDate} from "@/function/formatDate";
import {Adresse} from "@/ts/interface/compte";
import {CreateEngagement as EngagementInterface} from "@/ts/interface/detailOffre";
import {Option} from "@/ts/interface/detailOffre";
import {DetailOffre} from "@/ts/interface/detailOffre";

import EngagementElement from "./EngagementElement";

function Engagement({
  offre,
  updateOffre,
}: {
  offre: DetailOffre;
  updateOffre: (value: any) => void;
}): JSX.Element | undefined {
  const context = useContext(HookFormContext);

  if (!context) {
    throw new Error("HookFormContext is not provided.");
  }
  const {handleSubmit, getValues, control} = context;

  const {
    fields: fieldsEngagement,
    append: appendEngagement,
    remove: removeEngagement,
  } = useFieldArray({
    control,
    name: "engagements",
  });

  const [searchParams] = useSearchParams();
  const currentArticleId: string | null = searchParams.get("article_id");
  const [isAcceptCGV, setIsAcceptCGV] = useState<boolean>(false);
  const [userAdresses, setUserAdresses] = useState<{
    adresses: Adresse[];
  } | null>(null);
  const [error, setError] = useState<{error: boolean; errorMessage: string}>({
    error: false,
    errorMessage: "",
  });

  const [modaleInfo, setModaleinfo] = useState<{
    isOpen: boolean;
    message: JSX.Element | JSX.Element[] | undefined;
    variant: "primary" | "validate" | "negate";
    title: string;
    close: () => void;
  }>({
    isOpen: false,
    message: undefined,
    variant: "primary",
    title: "",
    close: () => {},
  });

  useEffect(() => {
    const getAdressesUser = async (): Promise<void> => {
      const reponse = await fetchAdresse();
      setUserAdresses(reponse || null);
    };
    getAdressesUser();
  }, []);
  useEffect(() => {
    if (fieldsEngagement.length <= 0) {
      handleAddEngagement();
    }
  }, [userAdresses, fieldsEngagement]);

  const {options, livraisons, condition_engagement, articles} = offre;
  const articleOptions: Option[] =
    currentArticleId && options
      ? findDataArticle(options, parseInt(currentArticleId))
      : {};

  const optionFound: Option = articleOptions[0];

  const {adresses} = userAdresses || {};

  function handleAddEngagement(): void {
    if (adresses) {
      const foundedDefaultAdresse = findAdresse(adresses);
      if (!currentArticleId) {
        throw "Article Id manquant dans l'url";
      }

      const newCurrentEngagement: EngagementInterface | null =
        foundedDefaultAdresse
          ? {
              adresse_livraison_id: foundedDefaultAdresse.adresse_id.toString(),
              adresse_facturation_id:
                foundedDefaultAdresse.adresse_id.toString(),
              articles: [
                {
                  article_id: parseInt(currentArticleId),
                  quantite: optionFound?.minimum || 1,
                },
              ],
            }
          : null;

      appendEngagement(newCurrentEngagement);
    }
  }

  const onSubmit = async (offre_id: string): Promise<void> => {
    const contentConfirmationModale: JSX.Element[] | undefined = getValues(
      "engagements",
    )?.flatMap((engagement: any, index: number) => {
      const adresseLivraison = findAdresse(
        adresses,
        engagement.adresse_livraison_id,
      );

      const allEngagements: JSX.Element[] | undefined =
        engagement?.dates_livraisons?.map(
          (date: {libelle: string; value: string}) => {
            const allArticle = engagement.articles.map(
              (
                article: {article_id: number; quantite: number},
                index: number,
              ) => {
                const articleIndex = offre.articles.findIndex(offreArticle => {
                  return offreArticle.article_id == article.article_id;
                });
                return (
                  <p
                    key={`${article.article_id} ${index}`}>{`${article.quantite} x  ${
                    offre?.articles[articleIndex]?.nom
                  }`}</p>
                );
              },
            );
            return (
              <div className="engagementList" key={index}>
                <h4>
                  Livraison : <span>{formatDate(date.value)}</span> au plus tard
                </h4>
                <h5>
                  Adresse : <span>{adresseLivraison?.value}</span>
                </h5>
                <div
                  className="engagementList-articles"
                  key={
                    engagement.adresse_facturation_id +
                    engagement.adresse_livraison_id +
                    index
                  }>
                  {allArticle}
                </div>
              </div>
            );
          },
        );
      return allEngagements || [];
    });

    // On fait une copy de l'engagement pour faire la mise en forme avant l'envoie à l'api
    const copyEngagements: EngagementInterface[] = structuredClone(
      getValues("engagements"),
    );
    copyEngagements.forEach(engagement => {
      const articlesInEngagement = engagement.articles;

      // On duplique les articles pour chaque livraison
      // et on ajoute la date de livraison dans l'article
      const updateArticlesInEngagement = articlesInEngagement.flatMap(
        article => {
          const articleByLivraison = engagement?.dates_livraisons?.map(
            livraison => {
              return {...article, date: livraison.value};
            },
          );

          return articleByLivraison || article;
        },
      );

      engagement.articles = updateArticlesInEngagement;
      delete engagement.dates_livraisons;
    });

    if (tryFormValidity(getValues("engagements") as EngagementInterface[])) {
      setModaleinfo({
        ...modaleInfo,
        isOpen: true,
        message: <Loader />,
        title: "Envoi en cours",
        variant: "primary",
      }); // Ouvre la modale

      try {
        const reponse = await postEngagements(offre_id, copyEngagements);
        console.log(copyEngagements);

        // Si la réponse est réussie
        setModaleinfo({
          ...modaleInfo,
          isOpen: true,
          message: contentConfirmationModale,
          title: "Votre engagement a bien été pris en compte",
          variant: "validate",
          close: () => {
            setModaleinfo({...modaleInfo, isOpen: false});
            removeEngagement();
          },
        });

        setIsAcceptCGV(false); // Met à jour la modale avec succès
        updateOffre(reponse || null);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err) {
        // En cas d'erreur
        setModaleinfo({
          ...modaleInfo,
          isOpen: true,
          message: <p>Votre engagement n'est pas pris en compte</p>,
          variant: "negate",
          title: "Une erreur s'est produite",
          close: () => {
            setModaleinfo({...modaleInfo, isOpen: false});
          },
        });
      }
    }
  };

  const CreateEngagementList: JSX.Element[] | undefined = fieldsEngagement?.map(
    (value: any, index: number) => {
      return (
        <EngagementElement
          key={`${value.id} ${index}`}
          adresses={adresses}
          indexEngagement={index}
          options={options}
          livraisons={livraisons}
          offreArticles={articles}
          removeEngagement={removeEngagement}
        />
      );
    },
  );
  const throwError = (condition: boolean, message: string): void => {
    if (condition) {
      throw message;
    }
    return;
  };
  const checkDateLivraison = (engagements: EngagementInterface[]): boolean => {
    let hasError = false;
    engagements.forEach(engagement => {
      if (!engagement.dates_livraisons) {
        hasError = true;
      }
    });
    return hasError;
  };
  const tryFormValidity = (
    engagements: EngagementInterface[],
  ): boolean | void => {
    // Empêche l'envoi du formulaire si les champs requis ne sont pas remplis
    try {
      throwError(
        !isAcceptCGV,
        "Vous devez accepter les Conditions générales de vente pour vous engager.",
      );
      throwError(
        checkDateLivraison(engagements),
        "Date de livraison manquante",
      );
      setError({
        error: false,
        errorMessage: "",
      });
      return true;
    } catch (err: any) {
      setError({error: true, errorMessage: err});
    }
  };
  return (
    <Section id="engagement">
      <BlocLabel label="Engagement" />
      <form
        onSubmit={handleSubmit(() => {
          console.log(getValues());
          if (offre.offre_id) {
            onSubmit(offre.offre_id.toString());
          }
        })}>
        <p className="engagement-attention">
          <span>
            ATTENTION : Les quantités seront envoyées à chaque date ( 2
            quantités et 4 dates = 8 unités reçues ).
          </span>
          <br />
          Pour des quantités différentes à chaque date, cliquez sur "Ajouter un
          engagement".
        </p>
        {CreateEngagementList}
        <div className="engagement-button">
          {adresses && adresses?.length > 0 ? (
            <Button
              label={"Ajouter un engagement"}
              icon={iconAdd}
              variant="secondary"
              onClick={() => handleAddEngagement()}
            />
          ) : (
            <ErrorMessageComponent text="Aucune adresse disponible. Pour ajouter une adresse, contactez Sabine Sansot" />
          )}
        </div>

        <div className="engagement-condition">
          <div className="engagement-condition--message">
            <p>{condition_engagement?.contenu}</p>
          </div>
          <div className="engagement-condition--confirmation">
            <div onClick={() => setIsAcceptCGV(!isAcceptCGV)}>
              <input
                type="checkbox"
                name="confirme_CGV"
                checked={isAcceptCGV}
                onChange={() => {}}
                required={true}
              />
              <label htmlFor="confirme_CGV">
                j'accepte les conditions d'engagement.
              </label>
            </div>

            <p>Votre engagement ne sera plus modifiable après validation</p>
          </div>
          {error.error ? (
            <ErrorMessageComponent text={error.errorMessage} />
          ) : null}
          <Button
            variant={"validate"}
            label="Je valide mon engagement"
            icon={iconValidate}
            onClick={() => {}}
            type="submit"
          />
          <Modale
            title={modaleInfo.title}
            isOpen={modaleInfo.isOpen}
            variant={modaleInfo.variant}
            close={modaleInfo.close}>
            {modaleInfo.message}
          </Modale>
        </div>
      </form>
    </Section>
  );
}

export default Engagement;
